import { Link, useNavigate } from 'react-router-dom';
import Button from 'components/form/Button';
import { InputWithStyle, InputWithoutControl } from 'components/form/Input';
import React, { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import SelectInput from 'components/form/SelectInput';
import countryList from 'react-select-country-list';
import { toast } from 'react-toastify';
import { createUser } from 'api/user';

const RegisterSchema = z.object({
  email: z.string().email({ message: 'Invalid email' }),
  password: z.string().min(8, 'password length should be a 8 or more'),
  fullName: z.string().min(3, 'name is too short'),
  country: z.string().min(1),
  terms: z.boolean(),
});

type RegisterSchemaType = z.infer<typeof RegisterSchema>;

const Register = () => {
  window.document.title = 'Login';
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid, isDirty },
  } = useForm<RegisterSchemaType>({
    resolver: zodResolver(RegisterSchema),
    mode: 'onTouched',
    defaultValues: {
      terms: false,
    },
  });

  const navigate = useNavigate();

  const options: any = useMemo(() => countryList().getData(), []);

  const countryListData =
    options.length &&
    options.map((item: any) => ({
      label: item.label,
      value: item.label,
    }));

  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [loading, setLoading] = useState(false);

  const terms = useWatch({ control, name: 'terms' });

  const submitFunction = async (data: RegisterSchemaType) => {
    setLoading(true);
    try {
      const res = await createUser(data);

      if (res?.data) {
        window.localStorage.setItem('email', data.email);
        navigate('/verify-email');
      }
    } catch (error: any) {
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
    setLoading(false);
  };

  const onSubmit = (data: RegisterSchemaType) => {
    submitFunction(data);
  };

  return (
    <section className="flex h-screen">
      <div className="flex w-full justify-center">
        <div className="hidden h-screen basis-[65%] lg:block">
          <img
            src="/assets/images/chart.jpg"
            alt="card"
            className="h-full w-full object-cover brightness-75"
          />
        </div>
        <div className="my-auto basis-full px-10 md:basis-1/2 lg:basis-[35%]">
          <h3 className="mb-8 text-2xl font-medium">Create Account</h3>
          <form className="flex flex-col gap-y-8">
            <div className="flex flex-col gap-y-1">
              <label htmlFor="name" className="font-semibold">
                Full name
              </label>
              <InputWithStyle
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                }
                name="fullName"
                placeholder="Enter full name"
                type="text"
                control={control}
              />
            </div>
            <div className="flex flex-col gap-y-1">
              <label htmlFor="email" className="font-semibold">
                Email
              </label>
              <InputWithStyle
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                    />
                  </svg>
                }
                name="email"
                type="email"
                placeholder="Enter email"
                control={control}
              />
            </div>

            <div className="flex flex-col gap-y-1">
              <label htmlFor="email" className="font-semibold">
                Country
              </label>
              <SelectInput
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.115 5.19l.319 1.913A6 6 0 008.11 10.36L9.75 12l-.387.775c-.217.433-.132.956.21 1.298l1.348 1.348c.21.21.329.497.329.795v1.089c0 .426.24.815.622 1.006l.153.076c.433.217.956.132 1.298-.21l.723-.723a8.7 8.7 0 002.288-4.042 1.087 1.087 0 00-.358-1.099l-1.33-1.108c-.251-.21-.582-.299-.905-.245l-1.17.195a1.125 1.125 0 01-.98-.314l-.295-.295a1.125 1.125 0 010-1.591l.13-.132a1.125 1.125 0 011.3-.21l.603.302a.809.809 0 001.086-1.086L14.25 7.5l1.256-.837a4.5 4.5 0 001.528-1.732l.146-.292M6.115 5.19A9 9 0 1017.18 4.64M6.115 5.19A8.965 8.965 0 0112 3c1.929 0 3.716.607 5.18 1.64"
                    />
                  </svg>
                }
                options={countryListData}
                name="country"
                label="Select country"
                control={control}
                containerClass="brand-border-light brand-shadow bg-white"
                className="bg-white"
              />
            </div>
            <div className="flex flex-col gap-y-1">
              <label htmlFor="email" className="font-semibold">
                Password
              </label>
              <InputWithStyle
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                }
                endIcon={
                  <Button
                    type="button"
                    onClick={() => setPasswordVisibility(!passwordVisibility)}
                  >
                    {passwordVisibility ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    )}
                  </Button>
                }
                name="password"
                placeholder="Enter password"
                type={passwordVisibility ? 'text' : 'password'}
                control={control}
              />
            </div>
            <div>
              <label className="flex cursor-pointer items-center justify-center gap-y-1">
                <InputWithoutControl
                  name="terms"
                  type="checkbox"
                  containerClass="border-none"
                  handleChange={() => setValue('terms', !terms)}
                  value={terms}
                />
                Agree to terms and conditions
              </label>
            </div>
            <Button
              type="button"
              className="rounded-md bg-blue-500 py-2 text-white"
              disabled={!isDirty || !isValid || !terms || loading}
              onClick={handleSubmit(onSubmit)}
            >
              Create account
            </Button>
          </form>
          <br />
          <p className="my-2 mx-auto border"></p>
          <p>
            Already have an account?{' '}
            <Link to="/login">
              <span className="text-blue-500">login</span>
            </Link>
          </p>
          <p className="mt-2 text-sm">
            <Link to="/">
              <span className="text-blue-500">blockchain</span>
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Register;
